/**
 * Pure (React-free) logic for the plan-change confirmation dialog: the quota-resolution
 * state, its translation into an API call, and validation of the site choice. Testable
 * without a DOM.
 */
import { UNLIMITED, type AddOnTargetInput, type EntitlementImpact, type QuotaOptions } from "../types";

/**
 * How the customer covers a shrinking site quota:
 *  - `addon` - buy the missing number of "+1 site" add-ons, every site stays live;
 *  - `keep`  - give sites up and choose which ones survive.
 */
export type QuotaMode = "addon" | "keep";

export interface QuotaResolution {
  mode: QuotaMode;
  /** Sites that stay live (only meaningful in `keep` mode). */
  keepIds: string[];
}

/**
 * Initial choice: default to buying the add-on, because that preserves what the customer
 * already has. When the add-on is unavailable (free target plan, unsynced price) the site
 * picker is the only remaining path.
 */
export function defaultResolution(quota: QuotaOptions | null | undefined): QuotaResolution {
  return { mode: quota?.extraSite ? "addon" : "keep", keepIds: [] };
}

/** How many sites may be picked in `keep` mode. Unlimited -> all of them. */
export function keepCapacity(quota: QuotaOptions | null | undefined): number {
  if (!quota) return 0;
  return quota.allowed === UNLIMITED ? quota.sites.length : quota.allowed;
}

/** Whether the choice can be submitted: at least one site, never more than the quota. */
export function keepIsComplete(resolution: QuotaResolution, quota: QuotaOptions | null | undefined): boolean {
  if (resolution.mode !== "keep") return true;
  const capacity = keepCapacity(quota);
  return resolution.keepIds.length > 0 && resolution.keepIds.length <= capacity;
}

/** Toggle a site in or out of the selection, respecting the quota ceiling. */
export function toggleKeep(resolution: QuotaResolution, siteId: string, capacity: number): QuotaResolution {
  const has = resolution.keepIds.includes(siteId);
  if (has) return { ...resolution, keepIds: resolution.keepIds.filter((id) => id !== siteId) };
  if (resolution.keepIds.length >= capacity) return resolution; // ceiling reached - the click is a no-op
  return { ...resolution, keepIds: [...resolution.keepIds, siteId] };
}

/**
 * Add-on targets for the API. `addon` mode -> the absolute quantity (existing + missing);
 * `keep` mode -> none (the plan changes on its own). The API always takes ABSOLUTE target
 * quantities, never deltas.
 */
export function addOnTargets(resolution: QuotaResolution, quota: QuotaOptions | null | undefined): AddOnTargetInput[] {
  if (resolution.mode !== "addon" || !quota?.extraSite) return [];
  return [{ code: quota.extraSite.code, quantity: quota.extraSite.targetQuantity }];
}

/** Total cost of the extra sites in the chosen cycle (cents). */
export function extraSiteTotalCents(quota: QuotaOptions | null | undefined): number {
  if (!quota?.extraSite) return 0;
  return quota.extraSite.unitPriceCents * quota.extraSite.quantityNeeded;
}

/** Whether the dialog needs to show the quota block at all. */
export function needsQuotaResolution(quota: QuotaOptions | null | undefined): quota is QuotaOptions {
  return Boolean(quota && quota.deficit > 0);
}

/**
 * Display value of a limit, with its unit. Storage is stored in MB; above 1000 MB we show
 * GB, because "14000 MB" reads as noise. Other keys are plain counts (images, videos,
 * tokens).
 */
export function formatLimitValue(key: string, value: number, locale: "et" | "en"): string {
  if (value === UNLIMITED) return "∞";
  const nf = new Intl.NumberFormat(locale === "et" ? "et-EE" : "en-US");
  if (key === "storage.mb") {
    return value >= 1000 ? `${nf.format(Math.round((value / 1000) * 10) / 10)} GB` : `${nf.format(value)} MB`;
  }
  return nf.format(value);
}

/** Whether the impact list has anything worth rendering. */
export function hasImpacts(impacts: { entitlements: EntitlementImpact[]; lostFeatures: string[] } | null | undefined): boolean {
  return Boolean(impacts && (impacts.entitlements.length > 0 || impacts.lostFeatures.length > 0));
}
